// oxlint-disable no-console
import React, { useEffect, useState, useCallback } from 'react';
import { observer } from 'mobx-react';

import CircularProgress from '@mui/material/CircularProgress';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SyncIcon from '@mui/icons-material/Sync';
import CloudDoneIcon from '@mui/icons-material/CloudDone';

import { Button } from 'components/Button';

import CustomModal from 'components/CustomModal';

import { publicEventFormDB } from 'utils/indexedDB/publicEventFormDB';

import { DEFAULT_TIME_FORMAT } from 'core/constants';

import { formatMoment } from 'utils/formatters/timezone';

import {
  QueueList,
  QueueItem,
  QueueItemInfo,
  QueueItemTitle,
  QueueItemTime,
  StatusBadge,
  EmptyState,
  EmptyStateIcon,
  EmptyStateText,
  QueueSummary,
  SummaryText,
} from './styles';
import { OfflineQueueItem, OfflineQueueModalProps, StatusType } from './types';

const getStatusDisplay = (status: string | undefined): { label: string; status: StatusType } => {
  switch (status) {
    case 'submitted':
      return { label: 'Submitted', status: 'submitted' };
    case 'failed':
    case 'template_mismatch':
      return { label: 'Failed', status: 'failed' };
    case 'in_queue':
    case 'pending':
      return { label: 'Pending', status: 'pending' };
    default:
      return { label: 'Pending', status: 'pending' };
  }
};

const StatusIcon: React.FC<{ status: StatusType }> = ({ status }) => {
  switch (status) {
    case 'submitted':
      return <CheckCircleOutlineIcon fontSize="small" />;
    case 'failed':
      return <ErrorOutlineIcon fontSize="small" />;
    case 'syncing':
      return <SyncIcon fontSize="small" />;
    case 'pending':
    default:
      return <HourglassEmptyIcon fontSize="small" />;
  }
};

const LoadingState = (): React.ReactElement => (
  <EmptyState>
    <CircularProgress size={32} />
  </EmptyState>
);

const EmptyQueueState = (): React.ReactElement => (
  <EmptyState>
    <EmptyStateIcon>
      <CloudDoneIcon sx={{ fontSize: 48 }} />
    </EmptyStateIcon>
    <EmptyStateText>No pending submissions</EmptyStateText>
    <EmptyStateText>All your submissions have been synced</EmptyStateText>
  </EmptyState>
);

interface QueueListContentProps {
  queueItems: OfflineQueueItem[];
  failedCount: number;
}

const QueueListContent = observer(
  ({ queueItems, failedCount }: QueueListContentProps): React.ReactElement => (
    <>
      <QueueSummary>
        <SummaryText>
          {queueItems.length} submission{queueItems.length !== 1 ? 's' : ''} in queue
          {failedCount > 0 && ` (${failedCount} failed)`}
        </SummaryText>
      </QueueSummary>
      <QueueList>
        {queueItems.map((item) => {
          const { label, status } = getStatusDisplay(item.submission?.status);

          return (
            <QueueItem key={item.uuid}>
              <QueueItemInfo>
                <QueueItemTitle>Submission {item.event_submission_id.slice(0, 8)}...</QueueItemTitle>
                <QueueItemTime>{formatMoment(item.createdAt, DEFAULT_TIME_FORMAT)}</QueueItemTime>
              </QueueItemInfo>
              <StatusBadge $status={status}>
                <StatusIcon status={status} />
                {label}
              </StatusBadge>
            </QueueItem>
          );
        })}
      </QueueList>
    </>
  )
);

const OfflineQueueModal: React.FC<OfflineQueueModalProps> = ({ open, onClose }): React.ReactElement => {
  const [queueItems, setQueueItems] = useState<OfflineQueueItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadQueueItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const items = await publicEventFormDB.getOfflineQueueWithSubmissions();
      setQueueItems(items);
    } catch (error) {
      console.error('Failed to load offline queue:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadQueueItems();
    }
  }, [open, loadQueueItems]);

  const isFailed = (item: OfflineQueueItem): boolean => {
    const { status } = item.submission || {};
    return status === 'failed' || status === 'template_mismatch';
  };

  const failedCount = queueItems.filter(isFailed).length;

  const renderContent = (): React.ReactElement => {
    if (isLoading) {
      return <LoadingState />;
    }
    if (queueItems.length === 0) {
      return <EmptyQueueState />;
    }
    return <QueueListContent queueItems={queueItems} failedCount={failedCount} />;
  };

  return (
    <CustomModal
      openModal={open}
      onClose={onClose}
      modalTitle="Offline Submission Queue"
      modalDescription="View the status of your offline submissions. They will be sent when online."
      submitButton={
        <Button id="offline-queue-close-button" onClick={onClose} variant="contained" size="large" fullWidth>
          Close
        </Button>
      }
    >
      {renderContent()}
    </CustomModal>
  );
};

export default OfflineQueueModal;
